import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import { Alert, AlertRecordWithDate, AlertType } from 'lib/alert/models';
import filter from 'lodash/filter';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Flex from 'ui/Flex/Flex';
import FixedThresholdEvolutionNoDimension from './FixedThreshold/FixedThresholdEvolutionNoDimension';
import FixedThresholdEvolutionWithDimension from './FixedThreshold/FixedThresholdEvolutionWithDimension';

const DAY_IN_A_WEEK = 7;

/**
 * Return the records that are dated between today and today+nbDays.
 * @param records - The records to filter.
 * @param nbDays - The number of days from today which should be included in the returned records.
 */
const filterByNbDaysFromToday = (
    records: AlertRecordWithDate[],
    nbDays: number,
) => {
    const today = Date.now();
    return filter(
        records,
        (r) => differenceInCalendarDays(today, r.date) < nbDays,
    );
};

type GenericEvolutionProps = {
    alert: Alert;
    records: AlertRecordWithDate[];
    hasDimension: boolean;
};

const StyChapterTitle = styled(Typography, {
    name: 'StyChapterTitle',
})({
    marginBottom: '0.5rem',
    marginTop: '1rem',
});

const GenericAlertEvolution = ({
    alert,
    records,
    hasDimension,
}: GenericEvolutionProps) => {
    const { t } = useTranslation();
    const [nbDays, setNbDays] = useState(DAY_IN_A_WEEK);
    const limitedRecords = filterByNbDaysFromToday(records, nbDays);
    const title = (
        <StyChapterTitle variant="h5">
            {t('alert.status.history')}
        </StyChapterTitle>
    );

    const nbDaySelector = (
        <Flex sx={{ ml: 'auto', alignItems: 'center' }}>
            <Typography sx={{ mr: 1 }}>sur</Typography>
            <Select
                value={nbDays}
                onChange={(value) =>
                    setNbDays(
                        typeof value.target.value === 'number'
                            ? value.target.value
                            : parseInt(value.target.value, 10),
                    )
                }
                variant="standard"
            >
                <MenuItem value={7}>les 7 derniers jours</MenuItem>
                <MenuItem value={14}>les 14 derniers jours</MenuItem>
                <MenuItem value={30}>les 30 derniers jours</MenuItem>
            </Select>
        </Flex>
    );

    let evolutionComponent;
    if (alert.type === AlertType.FIXED_THRESHOLD_CONDITION) {
        if (hasDimension) {
            evolutionComponent = (
                <FixedThresholdEvolutionWithDimension
                    records={limitedRecords}
                    nbDays={nbDays}
                    alert={alert}
                    firstRecordDate={
                        records.length > 0
                            ? records[records.length - 1].date
                            : new Date()
                    }
                />
            );
        } else {
            evolutionComponent = (
                <FixedThresholdEvolutionNoDimension
                    records={limitedRecords}
                    alert={alert}
                />
            );
        }
    }
    return (
        <>
            <Flex sx={{ alignItems: 'center', mb: 2, mt: 2 }}>
                {title}
                {nbDaySelector}
            </Flex>
            {evolutionComponent}
        </>
    );
};

export default GenericAlertEvolution;
